import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
	type BrandNearMatch,
	findBrandNearMatches,
} from "@/features/catalog/services/catalog-api.service";
import { useDebounce } from "@/lib/use-debounce";

const REASON: Record<BrandNearMatch["reason"], string> = {
	"same-name": "same name apart from punctuation or a suffix",
	"same-house": "same first word",
};

/**
 * Brands already present under a nearly identical name (issue #300).
 *
 * A WARNING, NEVER A BLOCK. Four unrelated companies are called `Anchor`, so
 * the only correct behaviour is to show the family and let the operator
 * decide; nothing here touches the Save button.
 *
 * `name` is null until the operator has typed one. Looking up a saved name
 * nobody touched would list a brand's siblings on every visit, and a panel
 * that is always there is a panel nobody reads.
 */
export function BrandNearMatches({
	name,
	excludeBrandId,
}: {
	name: string | null;
	/** The brand being renamed, so it is never reported as its own match. */
	excludeBrandId?: string;
}) {
	const term = useDebounce(name ?? "", 300).trim();
	const { data: matches } = useQuery({
		queryKey: ["admin-brand-near-matches", term, excludeBrandId ?? null],
		queryFn: () => findBrandNearMatches(term, excludeBrandId),
		enabled: term.length > 0,
	});

	if (!term || !matches || matches.length === 0) return null;

	return (
		<section
			aria-label="Similar brands"
			className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
		>
			<p>
				{matches.length === 1
					? "A brand with a similar name already exists."
					: `${matches.length} brands with a similar name already exist.`}{" "}
				Check this is not one of them. You can still save.
			</p>
			<ul className="grid gap-1">
				{matches.map((match) => (
					<li key={match.id}>
						<Link
							to={`/catalog/brands/${match.id}`}
							className="font-medium underline"
						>
							{match.name}
						</Link>{" "}
						— {match.productCount} product{match.productCount === 1 ? "" : "s"},{" "}
						{REASON[match.reason]}
					</li>
				))}
			</ul>
		</section>
	);
}
